import { Formik } from 'formik';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import { FiChevronRight, FiPlus } from 'react-icons/fi';

import LFModal from 'components/common/LFModal';
import Loading from 'components/common/loading/Loading';
import InputWrapper from 'components/common/inputWrapper';
import Input from 'components/common/inputWrapper/components/Input';

import { ADD_SKILL, EDIT_SKILL, LAST_SKILL_LEVEL } from 'constants/skills';

import { addSkillSchema } from 'schemas/addSkillSchema';

import * as skillV2Service from 'services/skillV2';

import { success } from 'utils/toast';
import { handleError } from 'utils/errorHandler';

import UseGlobalRating from './UseGlobalRating';

interface LocalRatingItem {
  rating: number;
  description: string;
}

interface SkillV2Node {
  id?: number;
  name?: string;
  description?: string;
  isEndSkill?: boolean;
  localRating?: LocalRatingItem[];
}

interface ParentNode {
  id: number;
  name: string;
}

interface FormValues {
  name: string;
  description: string;
  isGlobalRatingChecked: boolean;
  localRating: LocalRatingItem[];
}

interface Props {
  skillData?: SkillV2Node | null;
  skillParentData?: ParentNode[] | null;
  addSubSkill: (newParentData: ParentNode[]) => void;
  isOpen: boolean;
  onClose: () => void;
  callback?: (result?: { message?: string }) => void;
}

const AddSkillModal: React.FC<Props> = (props) => {
  const { skillData, skillParentData, addSubSkill, isOpen, onClose, callback } = props;
  const resolvedSkillParentData = skillParentData ?? [];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const resetModalState = () => {
    setIsSubmitting(false);
    setIsDeleteConfirmationModalOpen(false);
    setIsDeleting(false);
  };

  const handleClose = () => {
    resetModalState();
    onClose();
  };

  const defaultLocalRating: LocalRatingItem[] = useMemo(
    () => [
      { rating: 1, description: '' },
      { rating: 2, description: '' },
      { rating: 3, description: '' },
      { rating: 4, description: '' },
    ],
    []
  );

  const initialValues: FormValues = {
    name: skillData?.name || '',
    description: skillData?.description || '',
    isGlobalRatingChecked: skillData?.localRating?.length ? false : true,
    localRating: skillData?.localRating || defaultLocalRating,
  };

  const lastParent = resolvedSkillParentData[resolvedSkillParentData.length - 1];
  const isEditMode = Boolean(skillData?.id);
  const skillLevel = resolvedSkillParentData.length;
  const lastSkillLevel = skillLevel === LAST_SKILL_LEVEL;

  const handleAddSubSkill = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (!skillData?.id || !skillData.name) {
      return;
    }

    addSubSkill([...resolvedSkillParentData, { id: skillData.id, name: skillData.name }]);
  };

  const handleSubmit = async (body: FormValues) => {
    const refineBody = {
      name: body.name,
      description: body.description,
      ...(body.isGlobalRatingChecked ? {} : { localRating: body.localRating }),
    };

    try {
      setIsSubmitting(true);

      if (!isEditMode) {
        if (!lastParent) {
          return;
        }

        const updatedBody = { ...refineBody, parentId: lastParent.id };
        const data = (await skillV2Service.create(updatedBody)) as { message?: string };

        callback?.(data);
        handleClose();
        success({ title: 'Success', message: data.message });
      } else {
        const data = (await skillV2Service.update(skillData?.id ?? 0, refineBody)) as { message?: string };

        callback?.(data);
        handleClose();
        success({ title: 'Success', message: data.message });
      }
    } catch (err) {
      handleError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const showDeleteConfirmationModal = () => {
    setIsDeleteConfirmationModalOpen(true);
  };

  const closeDeleteConfirmationModal = () => {
    setIsDeleteConfirmationModalOpen(false);
  };

  const onDelete = async () => {
    if (!isEditMode || !skillData?.id) {
      return;
    }

    try {
      setIsDeleting(true);

      await skillV2Service.deleteSkill(skillData.id);

      success({
        title: 'Success',
        message: 'The skill has been deleted successfully.',
      });

      callback?.();
    } catch (error) {
      handleError(error);
    } finally {
      handleClose();
    }
  };

  const SkillList: React.FC<{ skills: ParentNode[] }> = ({ skills }) => {
    return (
      <div className="skill-modal__skill-list">
        {skills.map((skill, index) => {
          const lastItem = index === skills.length - 1;

          return (
            <div className="skill-modal__skill-item" key={skill.id}>
              <div className={classNames({ 'skill-modal--semi-bold': lastItem })}>{skill.name}</div>
              {!lastItem && <FiChevronRight className="p-2" size={12} />}
            </div>
          );
        })}
      </div>
    );
  };

  const TriggerBtn = () => {
    return <div className="hidden"></div>;
  };

  return (
    <LFModal
      TriggerBtn={TriggerBtn}
      isOpen={isOpen}
      onClose={handleClose}
      closeButton={true}
      title={{ text: isEditMode ? EDIT_SKILL : ADD_SKILL, type: 'info' }}
      className="skill-modal"
      htmlContent={
        <Formik<FormValues>
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={addSkillSchema}
          validateOnChange={false}
          validateOnBlur={false}
          enableReinitialize
        >
          {({ handleSubmit, handleBlur, handleChange, errors, values, dirty, setFieldValue }) => {
            const localRatingErrors = Array.isArray(errors.localRating)
              ? errors.localRating.map((err) =>
                  typeof err === 'string' ? { description: err } : { description: err.description }
                )
              : undefined;

            return (
              <form onSubmit={handleSubmit} className="skill-modal__form">
                <div className="skill-modal__form-body skill-modal__skill-body custom-scroll">
                  <div>
                    <div className="skill-modal__label">You are {isEditMode ? 'editing' : 'adding'} skill under:</div>
                    <SkillList skills={resolvedSkillParentData} />
                  </div>

                  <Input
                    isMandatory
                    label="Skill Name"
                    name="name"
                    placeholder="Type skill name"
                    className="wp-100"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.name}
                    disabled={isSubmitting}
                    multiline={false}
                    type="text"
                  />

                  <InputWrapper label="Description" error={errors.description}>
                    <textarea
                      name="description"
                      placeholder="Type skill description here..."
                      className={classNames('form-elem custom-scroll-bar skill-modal__textarea', {
                        'form-elem--error': Boolean(errors.description),
                      })}
                      value={values.description}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </InputWrapper>

                  {(Boolean(skillData?.isEndSkill) || !isEditMode) && (
                    <UseGlobalRating
                      values={{ isGlobalRatingChecked: values.isGlobalRatingChecked, localRating: values.localRating }}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      errors={{ localRating: localRatingErrors }}
                      setFieldValue={(field, value) => setFieldValue(field, value)}
                    />
                  )}

                  {isEditMode && !lastSkillLevel && (
                    <div className="skill-modal__sub-skill" onClick={handleAddSubSkill}>
                      <button className="btn skill-modal__sub-skill-btn" type="button">
                        <FiPlus size={12} fontWeight={600} />
                        Add Sub-skill
                      </button>
                      {values.name && Boolean(skillData?.isEndSkill) && (
                        <div className="skill-modal__sub-skill-note">
                          Note that, <span>{values.name} </span> will be converted into a skill group under{' '}
                          <span>{lastParent?.name}</span> to have its own set of skills.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="skill-modal__footer">
                  <button
                    type="submit"
                    onClick={() => handleSubmit()}
                    className={classNames('btn skill-modal__btn', {
                      'btn--primary': !isSubmitting && dirty,
                      'btn--disabled': isSubmitting || !dirty,
                    })}
                    disabled={isSubmitting || !dirty}
                  >
                    <span className={classNames({ invisible: isSubmitting })}>{isEditMode ? 'Save' : 'Add'}</span>
                    {isSubmitting && <Loading />}
                  </button>

                  <button
                    type="button"
                    className={classNames('btn ml-10 skill-modal__btn', {
                      'btn--outlined-grey': !isSubmitting,
                      'btn--disabled': isSubmitting,
                    })}
                    onClick={(e) => {
                      e.preventDefault();
                      handleClose();
                    }}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>

                  {isEditMode && Boolean(skillData?.isEndSkill) && (
                    <button
                      type="button"
                      className="btn btn--outlined-danger f-right card-button change-tentative-exit__btn"
                      onClick={showDeleteConfirmationModal}
                      disabled={isSubmitting}
                    >
                      Delete
                    </button>
                  )}
                </div>

                {isDeleteConfirmationModalOpen && (
                  <div className="skill-delete-modal absolute">
                    <div className="font-14">
                      <p>
                        {'Are you sure you want to delete the skill: '} <b>{values.name}</b>
                        {'?'}
                      </p>
                      <p>{'It can not be undone once deleted.'}</p>
                    </div>
                    <div className="d-flex gap-4x mt-4 f-right">
                      <button
                        type="button"
                        className={classNames('btn ml-10 skill-modal__btn', {
                          'btn--outlined-grey': !isSubmitting,
                          'btn--disabled': isSubmitting,
                        })}
                        onClick={closeDeleteConfirmationModal}
                        disabled={isDeleting}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn--outlined-danger f-right card-button min-width-80"
                        onClick={onDelete}
                        disabled={isDeleting}
                      >
                        {isDeleting ? <Loading /> : 'Yes, delete'}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            );
          }}
        </Formik>
      }
    />
  );
};

export default AddSkillModal;
